import { FC, useState, useContext } from "react";
// Libraries
import { useMutation } from "@apollo/client";
import { Formik } from "formik";
import { DateTime } from "luxon";
// API
import { GetAnimalsList_animalsExtended_animals } from "api/MyLivestock/Animal/types/GetAnimalsList";
import { GetContactList_contacts } from "api/AddressBook/types/GetContactList";
import { ContactContactType } from "api/graphql-global-types";
import { LOG_ACTIVITY_BULK_MUTATION } from "api/MyLivestock/Activity/mutations";
import { GET_MY_ANIMALS_LIST } from "api/MyLivestock/Animal/queries";
// Utils
import { CommonContext } from "config/commonProvider";
import { ACTIVITY_TEMPLATE_PREVIOUS_KEEPER, ADD_PREVIOUS_KEEPER_TEMPLATE } from "constants/Animals";
import { getBUFromLocalStorage } from "helpers/storage";
import { formatAddressString } from "components/Settings/AddressBook/AddressListTable";
// Components
import { Button, Modal, Spacer, Text, Flex } from "components";
import { AddressBookSelect } from "components/Settings/AddressBook/AddressBookSelect";
import {
  AddAddressFormValues,
  initialValuesDefault,
  NewContactModalContent,
} from "components/Settings/AddressBook/NewContactModalContent";
import { useGetActivityTemplates } from "hooks";

interface ConfirmAddPreviousKeeperModalProps {
  animals: GetAnimalsList_animalsExtended_animals[];
  isShowModal?: boolean;
  isSingleAnimal?: boolean;
  onClose?: () => void;
}

export const ConfirmAddPreviousKeeperModal: FC<ConfirmAddPreviousKeeperModalProps> = ({
  animals = [],
  isShowModal = false,
  isSingleAnimal = false,
  onClose,
}) => {
  const [showModal, setShowModal] = useState(isShowModal);
  const businessUnit = getBUFromLocalStorage();
  const [showAddContactForm, setShowAddContactForm] = useState(false);
  const { showNotification } = useContext(CommonContext);

  const { data: activityTemplates } = useGetActivityTemplates();

  const activityTemplate = activityTemplates?.find((el) => el.slug === ADD_PREVIOUS_KEEPER_TEMPLATE);

  const animalsCanAddPreviousKeeper = animals.filter((a) => !a.hasPreviousKeeperInfo);
  const animalsIds = animalsCanAddPreviousKeeper.map((item) => item.id);

  const [logActivityBulk, { loading }] = useMutation(LOG_ACTIVITY_BULK_MUTATION);

  const handleModalToggle = (): void => {
    setShowModal(!showModal);
    onClose && onClose();
  };

  const handleConfirmClick = async (values): Promise<void> => {
    const fromBusinessUnitName = activityTemplate?.fields.find(
      (field) => field.slug === ACTIVITY_TEMPLATE_PREVIOUS_KEEPER.fromBUName,
    );
    const fromBusinessUnitAddress = activityTemplate?.fields.find(
      (field) => field.slug === ACTIVITY_TEMPLATE_PREVIOUS_KEEPER.fromBUAddress,
    );
    const sourceHoldingId = activityTemplate?.fields.find(
      (field) => field.slug === ACTIVITY_TEMPLATE_PREVIOUS_KEEPER.sourceHoldingId,
    );
    const address = formatAddressString(values);
    try {
      const { errors } = await logActivityBulk({
        variables: {
          input: {
            date: DateTime,
            activityTemplate: Number(activityTemplate?.id),
            animals: animalsIds,
            businessUnit,
            payload: {
              data: [
                { id: Number(fromBusinessUnitName?.id), value: values.name },
                { id: Number(fromBusinessUnitAddress?.id), value: address || null },
                { id: Number(sourceHoldingId?.id), value: values.farmId || null },
              ],
              name: "Add Previous Keeper",
            },
          },
        },
        update: (
          cache,
          {
            data: {
              logActivityBulk: { errors },
            },
          },
        ) => {
          if (!errors) {
            const updatedIds = new Set(animalsIds);
            const previousData = cache.readQuery({
              query: GET_MY_ANIMALS_LIST,
              variables: {
                businessUnitId: businessUnit,
              },
            }) as {
              animalsExtended: {
                animals: GetAnimalsList_animalsExtended_animals[];
                animalsCount: number;
              };
            };

            const animalsExtended = previousData?.animalsExtended;

            if (animalsExtended) {
              const updatedAnimalsData = animalsExtended.animals.map((item) =>
                updatedIds.has(item.id) ? { ...item, hasPreviousKeeperInfo: true } : item,
              );

              cache.writeQuery({
                query: GET_MY_ANIMALS_LIST,
                variables: {
                  businessUnitId: businessUnit,
                },
                data: {
                  ...previousData,
                  animalsExtended: {
                    ...previousData.animalsExtended,
                    animals: updatedAnimalsData,
                    animalsCount: updatedAnimalsData.length,
                  },
                },
              });
            }
          }
        },
      });
      if (errors && errors.length > 0) {
        showNotification({
          variant: "error",
          message: errors[0].message,
        });
      } else {
        handleModalToggle();
        showNotification({
          message: "Previous Keeper successfully added",
        });
        setShowAddContactForm(false);
      }
    } catch (e) {
      showNotification({
        variant: "error",
        message: "Error adding Previous Keeper",
      });
    }
  };

  const handleFormSubmit = (values: AddAddressFormValues): void => {
    handleConfirmClick(values);
  };

  return (
    <>
      {!isSingleAnimal && <Button caption="Add Previous Keeper" onClick={handleModalToggle} />}

      <Modal buttonsLeftSide active={showModal} handleClose={handleModalToggle} title="Add Previous Keeper" subTitle="">
        <Formik
          enableReinitialize
          onSubmit={handleConfirmClick}
          initialValues={initialValuesDefault}
          validateOnChange={false}
        >
          {({ values, setValues }): JSX.Element => {
            const handleContactChange = (contact: GetContactList_contacts | undefined): void => {
              const newValues = {
                contact: contact?.id || "",
                name: contact?.name || "",
                organisationName: contact?.organisationName || "",
                addressLine1: contact?.addressLine1 || "",
                addressLine2: contact?.addressLine2 || "",
                farmId: contact?.farmId || "",
                city: contact?.city || "",
                country: contact?.country?.id || "",
                postcode: contact?.postcode || "",
                countryCode: contact?.country?.code || "",
                phoneNumber: contact?.phoneNumber || "",
                email: contact?.email || "",
              };
              const newOldValues = {
                ...values,
                ...newValues,
              };
              setValues(newOldValues);
            };
            return (
              <>
                <Text smaller>
                  You are about to assign a Previous Keeper to selected animals. <b>This cannot be undone.</b>
                </Text>
                <Spacer baselineHeight={1} />
                <AddressBookSelect
                  value={values.contact}
                  onContactChange={handleContactChange}
                  label=""
                  disabled={showAddContactForm}
                  contactType={ContactContactType.FARM}
                />
                <Spacer baselineHeight={1} />
                <Flex container containerDirection="column" containerAlignItems="center" xs={12}>
                  <h4>Or enter manually</h4>
                  <Text smaller>This address will be added to your address book</Text>
                </Flex>
                <NewContactModalContent
                  onToggleShowForm={setShowModal}
                  onSubmitAddPreviousKeeper={handleFormSubmit}
                  isLoading={loading}
                  isPreviousKeeper={true}
                  initialValues={values}
                />
              </>
            );
          }}
        </Formik>
      </Modal>
    </>
  );
};
